/**
 * The welcome-pack business card — one design, two faces.
 *
 * Written in the landing's spec-sheet language (card #fdfcfa, ink #1e2228,
 * hairline rules, mono eyebrow) and rendered with each site's own identity:
 * real logo, logoFont/heading Google Fonts and brand colour, exactly as the
 * deployed site wears them.
 *
 * Extracted from /lab/pack (src/pages/PackLab.tsx) so the invite page can show
 * a prospect their own card. The lab keeps its toggles and imports the faces
 * from here; the invite renders one fixed configuration.
 *
 * Print target: US business card 3.5in × 2in. The print-ready export (3.75 ×
 * 2.25 with 0.125in bleed) renders THIS component with `bleed` set — see
 * src/print/. On screen `bleed` is 0 and nothing about the card changes.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { packAsset } from './assets';
import { groundFromFlag, useLogoGround } from './logoGround';
import { PACK_INK, resolveLogoSurface, type LogoSurface } from './logoSurface';

/* ------------------------------------------------------------------ */
/* Shape                                                               */
/* ------------------------------------------------------------------ */

export interface PackBusiness {
  id: string;
  trade: string;
  name: string;
  owner: string;
  phone: string;
  city: string;
  domain: string;
  established?: number | null;
  /** The deployed site's actual (colorized) logo. */
  logo?: string | null;
  /** Hex baked into that logo URL, or null when the logo keeps its own colours. */
  logoTintHex?: string | null;
  /** That baked hex isn't the theme's primary — a retired palette or re-rolled theme. */
  logoTintStale?: boolean;
  /** True = symbol-only logo (no text inside) — the name must be rendered. */
  logomark?: boolean;
  /**
   * Whether the logo's own artwork/edges read as dark — `theme.logoDarkBackground`
   * off the site's bundle (fnd-lw0/fnd-p84), sampled once at generation time.
   * `undefined` on bundles that predate that flag, or an identity producer
   * that hasn't forwarded it yet: `LogoImage` falls back to the live canvas
   * probe (`useLogoGround`) in that case. See `groundFromFlag` in
   * `./logoGround` for the exact mapping.
   */
  logoDarkBackground?: boolean;
  /**
   * The logo's own measured background colour (`business.theme.logoBgHex`,
   * fnd-782n), or null. When it is a real, non-white colour, every face that
   * prints the logo paints itself in it — see `facePalette` — so the logo's
   * canvas and the stock become one surface, the way the site's header does.
   */
  logoBgHex?: string | null;
  /** The deployed site's typography (Google Font family names). */
  fonts?: { heading: string | null; logo: string | null };
  brand: { primary: string; accent: string };
}

export function tradeLabel(vertical: string) {
  if (vertical === 'hvac') return 'HVAC';
  return vertical.charAt(0).toUpperCase() + vertical.slice(1);
}

export function luminance(hex: string) {
  const n = parseInt(hex.replace('#', ''), 16);
  return (0.2126 * ((n >> 16) & 255) + 0.7152 * ((n >> 8) & 255) + 0.0722 * (n & 255)) / 255;
}

/**
 * Theme accents are sometimes surface tones (near-white) — useless as a card
 * accent on paper, so they fall back to the primary. A near-white primary
 * would make text invisible; that becomes ink.
 */
export function cardBrand(brand: { primary: string; accent: string }) {
  return {
    primary: luminance(brand.primary) > 0.72 ? '#1e2228' : brand.primary,
    accent: luminance(brand.accent) > 0.72 ? brand.primary : brand.accent,
  };
}

/**
 * Loads the given Google Font families into the page. Fontshare-hosted pairs
 * simply fall back to the system stack.
 */
export function useGoogleFonts(
  families: (string | null | undefined)[],
  /**
   * `swap` is right for a screen — the card paints immediately and re-paints
   * in the real face. A PDF gets one paint and no second chance, so the print
   * entry asks for `block`: the text simply does not render until the face is
   * there, and the readiness gate waits for it.
   */
  display: 'swap' | 'block' = 'swap'
) {
  const wanted = useMemo(
    () => Array.from(new Set(families.filter(Boolean) as string[])).sort(),
    [families]
  );
  useEffect(() => {
    if (!wanted.length) return;
    const url =
      'https://fonts.googleapis.com/css2?' +
      wanted.map(f => `family=${f.replace(/ /g, '+')}:wght@400;700`).join('&') +
      `&display=${display}`;
    const id = 'foundlio-pack-gfonts';
    let link = document.getElementById(id) as HTMLLinkElement | null;
    if (!link) {
      link = document.createElement('link');
      link.id = id;
      link.rel = 'stylesheet';
      document.head.appendChild(link);
    }
    if (link.href !== url) link.href = url;
  }, [wanted, display]);
}

/* ------------------------------------------------------------------ */
/* Geometry & options                                                  */
/* ------------------------------------------------------------------ */

/** 126 px per inch → 3.5in × 2in card. */
export const PPI = 126;
export const CARD_W = 3.5 * PPI; // 441
export const CARD_H = 2 * PPI; // 252
/** Printers ask for 0.125in of safe margin inside the trim. */
export const SAFE = 0.125 * PPI;

export type LayoutId = 'center' | 'left';
/**
 * Whether the front prints the business name as text. 'auto' trusts the
 * logo metadata: a logomark (symbol-only) or a missing logo needs the name;
 * a full logo already carries it.
 */
export type NameMode = 'auto' | 'show' | 'hide';
/**
 * 'site' = the logo exactly as the deployed site paints it (Cloudinary
 * e_colorize/e_tint transform). 'original' strips the tint — for judging how
 * many colorized logos collapse into unreadable one-colour silhouettes.
 * 'auto' = site tint, except a stale tint (a hex from a retired palette or a
 * theme that was re-rolled after the logo was painted, incl. the old fallback
 * blue 0072e5), which gets repainted in the theme's real primary.
 */
export type LogoMode = 'auto' | 'site' | 'original';

export const stripColorize = (url: string) =>
  url
    .replace(/e_colorize:\d+,co_rgb:[0-9a-fA-F]{6}\//g, '')
    .replace(/e_tint:\d+:(?:rgb:)?[0-9a-fA-F]{6}\//g, '');

/**
 * Repaints an already-tinted logo in `hex` — the same transform the generator bakes.
 * Host-agnostic (anchors on `/image/upload/`). On img.foundlio.com the Worker
 * serves the nearest PRE-BAKED tint within a small RGB distance, else the
 * untinted original — so `hex` should be a theme-table colour, which
 * `brand.primary` is.
 */
export const retint = (url: string, hex: string) => {
  const marker = '/image/upload/';
  const clean = stripColorize(url);
  const at = clean.indexOf(marker);
  if (at < 0) return clean;
  const cut = at + marker.length;
  return `${clean.slice(0, cut)}e_colorize:100,co_rgb:${hex.replace('#', '')}/${clean.slice(cut)}`;
};

/**
 * The same logo, asked of Cloudinary at print resolution.
 *
 * A site's logo URL is delivered for a screen: `q_auto,f_auto` hands back a
 * ~300px WebP, which is a mush of artefacts at 300 DPI. This asks for a PNG at
 * the real printed pixel count instead (300 DPI across `boxInches`, capped by
 * `c_limit` so a small source is never upscaled into worse).
 *
 * What it must NOT touch: `e_colorize` — that is the paint the site wears, and
 * stripping it prints a logo in colours the client has never seen. `e_trim`
 * likewise has to stay first in the chain (see memory: logo-tint-safety), so
 * the new components are spliced in AFTER whatever the URL already carries
 * rather than at the head of the transform list.
 *
 * Works the same on img.foundlio.com (R2 + a Worker speaking this grammar,
 * fnd-1nvj) and legacy res.cloudinary.com — it only touches the path. On
 * img.foundlio.com `f_png` keeps the SOURCE format (no PNG encoder there; a
 * JPEG logo stays a q_100 JPEG, a PNG stays PNG) and `dpr_` caps at 2, which is
 * why the print width is spelled out in pixels here rather than as a dpr.
 */
export function printLogoUrl(url: string, boxInches: number): string {
  const marker = '/image/upload/';
  const at = url.indexOf(marker);
  if (at < 0) return url;
  const cut = at + marker.length;
  const head = url.slice(0, cut);
  const segs = url.slice(cut).split('/');

  // Where the transform chain stops and the asset's own path begins. A
  // transform segment is a comma-list of `xx_…` components; `v1234` looks like
  // one but is the version, i.e. already the public id.
  let split = 0;
  while (
    split < segs.length &&
    segs[split].length > 0 &&
    !/^v\d+$/.test(segs[split]) &&
    segs[split].split(',').every(c => /^[a-z]{1,3}_/.test(c))
  ) {
    split++;
  }

  // Drop only the screen-delivery components — quality, format and the
  // screen-sized width/height. Everything else (e_trim, e_colorize, c_pad,
  // b_white…) is part of how this logo is meant to look, and e_trim in
  // particular has to keep its place at the head of the chain.
  const transforms = segs
    .slice(0, split)
    .map(seg =>
      seg
        .split(',')
        .filter(c => c !== 'q_auto' && !/^f_/.test(c) && !/^[wh]_\d+$/.test(c))
        .join(',')
    )
    .filter(Boolean);

  transforms.push(`f_png,q_100,c_limit,w_${Math.ceil(boxInches * 300)}`);
  return `${head}${transforms.join('/')}/${segs.slice(split).join('/')}`;
}

/** The hex Cloudinary baked into a logo URL, or null for an untinted asset. */
export const logoTintOf = (url?: string | null) => {
  const m = url?.match(/(?:co_rgb:|e_tint:\d+:(?:rgb:)?)([0-9a-fA-F]{6})/);
  return m ? `#${m[1].toLowerCase()}` : null;
};

export const showsName = (b: PackBusiness, nameMode: NameMode) =>
  nameMode === 'show' || (nameMode === 'auto' && (Boolean(b.logomark) || !b.logo));

/** The logo URL a face should actually print for the given mode, or null. */
export function resolveLogo(b: PackBusiness, logoMode: LogoMode): string | null {
  const raw = b.logo;
  if (!raw) return null;
  if (logoMode === 'original') return stripColorize(raw);
  if (logoMode === 'auto' && b.logoTintStale) return retint(raw, b.brand.primary);
  return raw;
}

export const siteFont = (b: PackBusiness, kind: 'logo' | 'heading'): React.CSSProperties => {
  const family =
    kind === 'logo' ? (b.fonts?.logo ?? b.fonts?.heading) : (b.fonts?.heading ?? b.fonts?.logo);
  // The portal defines `--font-space-grotesk` (its own font loader); a host
  // that doesn't — template-monster renders this package too, and has no
  // reason to know that variable name — falls through to the literal family
  // name and then a generic sans rather than the browser's UI font.
  return {
    fontFamily: family
      ? `'${family}', var(--font-space-grotesk, 'Space Grotesk'), sans-serif`
      : `var(--font-space-grotesk, 'Space Grotesk'), sans-serif`,
    letterSpacing: '-0.01em',
  };
};

/* ------------------------------------------------------------------ */
/* Face palette                                                        */
/* ------------------------------------------------------------------ */

/**
 * Every colour a pack face prints with. On plain stock these are exactly the
 * Tailwind zinc values the faces always used (zinc-200/400/500/600), so a
 * piece with no logo surface is pixel-identical to before. On a logo surface
 * (`b.logoBgHex` resolves — see ./logoSurface) the stock takes the logo's
 * colour and every line of type flips to the ink that reads on it.
 */
export interface FacePalette {
  /** The stock. */
  bg: string;
  /** Primary text: the name, the phone, the headline. */
  ink: string;
  /** Secondary text: eyebrow, owner line, the card's phone. */
  muted: string;
  /** Tertiary text: the "Est." tag, footnotes. */
  faint: string;
  /** Hairline rules. */
  rule: string;
  /** What the domain line and brand-coloured accents print in. */
  primary: string;
  /** Bottom rule / frame — the theme's accent, or the primary where it would vanish. */
  accent: string;
  /** The resolved surface, when the face is painted in the logo's colour. */
  surface: LogoSurface | null;
}

/**
 * The palette for a face that is about to print `logo` (null when the logo is
 * missing or failed to load — a face with no logo on it has no colour to
 * adopt and stays on its stock). `stock` is the piece's own default paper.
 */
export function facePalette(
  b: PackBusiness,
  logo: string | null,
  stock = '#fdfcfa'
): FacePalette {
  const surface = logo ? resolveLogoSurface(b.logoBgHex) : null;
  if (!surface) {
    return {
      bg: stock,
      ink: PACK_INK,
      muted: '#71717a',
      faint: '#a1a1aa',
      rule: '#e4e4e7',
      primary: b.brand.primary,
      accent: b.brand.accent,
      surface: null,
    };
  }
  return {
    bg: surface.bg,
    ink: surface.ink,
    muted: surface.muted,
    faint: surface.faint,
    rule: surface.rule,
    primary: surface.accent(b.brand.primary),
    accent: surface.accent(b.brand.accent),
    surface,
  };
}

/* ------------------------------------------------------------------ */
/* Bits                                                                */
/* ------------------------------------------------------------------ */

/**
 * The card's QR.
 *
 * With a `url` it is generated in the browser — `qrcode` is pulled in on a
 * dynamic import so the encoder never sits in the main bundle. Without one it
 * falls back to the lab's supplied art (public/lab/qr.svg, a test code
 * pointing at foundlio.com), which is what /lab/pack has always printed.
 */
export function Qr({
  size,
  url,
  className = '',
  vector = false,
}: {
  size: number;
  url?: string | null;
  className?: string;
  /**
   * Print path: emit the code as inline SVG instead of a raster data URL, so
   * the modules stay razor-edged at whatever DPI the PDF is rasterised at
   * (a 2× PNG is ~180 DPI on a 0.67in box, which is where scanners start to
   * struggle with a bad print run).
   */
  vector?: boolean;
}) {
  const [generated, setGenerated] = useState<string | null>(null);
  const [svg, setSvg] = useState<string | null>(null);

  useEffect(() => {
    if (!url) {
      setGenerated(null);
      setSvg(null);
      return;
    }
    let alive = true;
    const opts = {
      errorCorrectionLevel: 'M' as const,
      // Rendered at 2× the printed box so it stays crisp on retina and in
      // the 300 DPI export path.
      width: size * 2,
      // Modules of quiet zone. The white tile the card puts underneath adds
      // a little more; scanners want at least this much.
      margin: 2,
      color: { dark: '#1e2228ff', light: '#ffffffff' },
    };
    import('qrcode')
      .then(mod => {
        const q = mod.default ?? mod;
        return vector ? q.toString(url, { ...opts, type: 'svg' }) : q.toDataURL(url, opts);
      })
      .then(out => {
        if (!alive) return;
        if (vector) setSvg(out);
        else setGenerated(out);
      })
      .catch(() => {
        /* fall back to the static art */
      });
    return () => {
      alive = false;
    };
  }, [url, size, vector]);

  if (vector) {
    if (!svg) {
      return <span style={{ width: size, height: size }} className={`block ${className}`} />;
    }
    return (
      <span
        data-qr="1"
        aria-hidden="true"
        // The generated markup carries its own width/height; force it onto the
        // printed box so the SVG scales instead of sitting at its natural size.
        style={{ width: size, height: size, display: 'block' }}
        className={`[&>svg]:h-full [&>svg]:w-full ${className}`}
        dangerouslySetInnerHTML={{ __html: svg }}
      />
    );
  }

  const src = url ? generated : packAsset('/lab/qr.svg');
  if (!src) {
    // Reserve the box while the encoder loads so the back face never reflows.
    return <span style={{ width: size, height: size }} className={`block ${className}`} />;
  }
  return (
    <img
      data-qr="1"
      src={src}
      width={size}
      height={size}
      alt=""
      aria-hidden="true"
      className={className}
    />
  );
}

/** The logo, on its own tile when the asset carries a baked background. */
export function LogoImage({
  src,
  imgClass,
  align,
  onError,
  darkBackground,
  onSurface = false,
}: {
  src: string;
  imgClass: string;
  align: 'center' | 'start';
  onError: () => void;
  /** `PackBusiness.logoDarkBackground`, when known — skips the canvas probe. */
  darkBackground?: boolean;
  /**
   * The face is already painted in the logo's own background colour (see
   * `facePalette`): the logo blends in as it is, and a tile behind it would
   * only draw a seam. Forces the ground to none and skips the probe.
   */
  onSurface?: boolean;
}) {
  const ground = useLogoGround(src, onSurface ? 'none' : groundFromFlag(darkBackground));
  const img = <img src={src} alt="" className={imgClass} onError={onError} />;
  if (ground === 'none') return img;
  return (
    <span
      className={`inline-flex items-center justify-center rounded-[3px] px-2.5 py-1.5 ${
        align === 'center' ? 'self-center' : 'self-start'
      } ${ground === 'dark' ? 'bg-[#1e2228]' : 'bg-white ring-1 ring-zinc-200'}`}
    >
      {img}
    </span>
  );
}

function SafeAreaGuide({ show, bleed = 0 }: { show: boolean; bleed?: number }) {
  if (!show) return null;
  return (
    <div className="pointer-events-none absolute inset-0 z-20">
      <div
        className="absolute border border-dashed border-fuchsia-500/70"
        style={{ inset: SAFE + bleed }}
      />
      <span className="absolute left-1/2 top-0.5 -translate-x-1/2 font-mono text-[8px] uppercase tracking-[0.14em] text-fuchsia-600">
        safe area 0.125in
      </span>
    </div>
  );
}

function CardShell({
  children,
  guides,
  background = '#ffffff',
  w,
  h,
  bleed = 0,
}: {
  children: React.ReactNode;
  guides: boolean;
  background?: string;
  w?: number;
  h?: number;
  /**
   * Print only: grow the painted box by this many component px on every side,
   * so the stock and every bled element run past the trim line. Zero on
   * screen, where the card is a card and not a press sheet — and with zero the
   * shell is exactly what it always was.
   */
  bleed?: number;
}) {
  return (
    <div
      className={
        'relative overflow-hidden' +
        // Rounding and a cast shadow are screen furniture. On a bleeding sheet
        // they would print white corners and a grey smear into the trim.
        (bleed
          ? ''
          : ' rounded-[3px] shadow-[0_1px_3px_rgba(30,34,40,0.12),0_8px_24px_rgba(30,34,40,0.10)]')
      }
      style={{
        width: (w ?? CARD_W) + bleed * 2,
        height: (h ?? CARD_H) + bleed * 2,
        background,
        isolation: 'isolate',
      }}
    >
      {children}
      <SafeAreaGuide show={guides} bleed={bleed} />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* The card                                                            */
/* ------------------------------------------------------------------ */

export interface FaceProps {
  b: PackBusiness;
  guides: boolean;
  layout: LayoutId;
  nameMode: NameMode;
  logoMode: LogoMode;
  /**
   * Where the back's QR points. Real cards send the customer to the site's
   * review page; the lab has no per-client code, so it prints the static art.
   */
  qrUrl?: string | null;
  /**
   * Render at this size instead of the natural 3.5×2in footprint. The faces
   * are flex-based, so the card re-lays-out around the new box like a resized
   * div — used by the photo plate's `reflow` fit.
   */
  w?: number;
  h?: number;
  /** Print only — see CardShell. Zero (the default) is the screen card. */
  bleed?: number;
  /** Print only: the QR is emitted as inline SVG rather than a raster. */
  qrVector?: boolean;
}

export function CardFront({ b, guides, layout, nameMode, logoMode, w, h, bleed = 0 }: FaceProps) {
  // A dead logo URL (Cloudinary 400s exist in real bundles) must never leave
  // a blank card: treat it as no-logo so the name steps in. CardPair keys
  // this component by business id, so the flag resets on switch.
  const [logoBroken, setLogoBroken] = useState(false);
  const logo = logoBroken ? null : resolveLogo(b, logoMode);
  const name = showsName(logoBroken ? { ...b, logo: null } : b, nameMode);
  // The stock and every ink on it — the logo's own colour when it ships on a
  // solid canvas, the plain card otherwise (see facePalette).
  const p = facePalette(b, logo);
  const onSurface = p.surface !== null;
  const identity =
    layout === 'center' ? (
      <div className="flex flex-1 flex-col items-center justify-center gap-2 text-center">
        {logo ? (
          <LogoImage
            src={logo}
            align="center"
            // A mark printed above the name is half of a lockup, not the
            // whole identity — it has to sit back so the name reads first.
            imgClass={`${name ? 'max-h-[46px]' : 'max-h-[62px]'} max-w-[280px] object-contain`}
            onError={() => setLogoBroken(true)}
            darkBackground={b.logoDarkBackground}
            onSurface={onSurface}
          />
        ) : null}
        {name ? (
          <div
            className={`font-bold ${logo ? 'text-[14px] leading-tight' : 'text-[20px] leading-[1.05]'}`}
            style={{ ...siteFont(b, 'logo'), color: p.ink }}
          >
            {b.name}
          </div>
        ) : null}
        {b.owner ? (
          <div className="text-[11px]" style={{ color: p.muted }}>
            {b.owner}
          </div>
        ) : null}
      </div>
    ) : (
      <div className="flex flex-1 flex-col justify-center gap-1.5">
        {logo ? (
          <LogoImage
            src={logo}
            align="start"
            imgClass="max-h-[40px] max-w-[220px] object-contain object-left"
            onError={() => setLogoBroken(true)}
            darkBackground={b.logoDarkBackground}
            onSurface={onSurface}
          />
        ) : null}
        {name ? (
          <div
            className={`font-bold ${logo ? 'text-[13px]' : 'text-[20px] leading-[1.05]'}`}
            style={{ ...siteFont(b, 'logo'), color: p.ink }}
          >
            {b.name}
          </div>
        ) : null}
        {b.owner ? (
          <div className="text-[11px]" style={{ color: p.muted }}>
            {b.owner}
          </div>
        ) : null}
      </div>
    );

  return (
    <CardShell guides={guides} background={p.bg} w={w} h={h} bleed={bleed}>
      <div className="flex h-full flex-col" style={{ padding: SAFE + 2 + bleed }}>
        <div
          className="flex items-baseline justify-between border-b pb-2"
          style={{ borderColor: p.rule }}
        >
          <span
            className="font-mono text-[8px] uppercase tracking-[0.18em]"
            style={{ color: p.muted }}
          >
            {b.trade}
            {b.city ? ` · ${b.city}` : ''}
          </span>
          {b.established ? (
            <span
              className="font-mono text-[8px] uppercase tracking-[0.18em]"
              style={{ color: p.faint }}
            >
              Est. {b.established}
            </span>
          ) : null}
        </div>
        {identity}
        <div
          className="flex items-baseline justify-between border-t pt-2 font-mono text-[10px]"
          style={{ borderColor: p.rule }}
        >
          <span style={{ color: onSurface ? p.muted : '#52525b' }}>{b.phone}</span>
          <span style={{ color: p.primary }}>{b.domain}</span>
        </div>
      </div>
    </CardShell>
  );
}

export function CardBack({ b, guides, qrUrl, w, h, bleed = 0, qrVector }: FaceProps) {
  return (
    <CardShell guides={guides} background="#1e2228" w={w} h={h} bleed={bleed}>
      <div
        className="flex h-full items-center justify-between"
        style={{ padding: SAFE + 10 + bleed }}
      >
        <div className="max-w-[52%]">
          <div className="font-mono text-[8px] uppercase tracking-[0.18em] text-zinc-400">
            How did we do?
          </div>
          <div
            className="mt-2 text-[15px] font-bold leading-snug text-white"
            style={siteFont(b, 'heading')}
          >
            Two minutes of your time helps a local business.
          </div>
          <div className="mt-2.5 font-mono text-[9.5px] text-zinc-500">{b.domain}</div>
        </div>
        <div className="rounded-[3px] bg-white p-2">
          <Qr size={84} url={qrUrl} vector={qrVector} />
        </div>
      </div>
      {/* The accent rule bleeds off the bottom edge: on a press sheet it has to
          run past the trim, or a 0.4mm cutting drift leaves a white hairline. */}
      <div
        className="absolute inset-x-0 bottom-0"
        style={{ height: 3 + bleed, background: b.brand.accent }}
      />
    </CardShell>
  );
}

export function CardPair(props: FaceProps) {
  return (
    <div className="flex flex-wrap gap-5">
      <CardFront key={props.b.id} {...props} />
      <CardBack {...props} />
    </div>
  );
}

/** Both faces, side by side above ~920px, stacked and scaled below it. */
export function ResponsiveCardPair(props: FaceProps) {
  const host = React.useRef<HTMLDivElement | null>(null);
  const [avail, setAvail] = useState(0);

  useEffect(() => {
    const node = host.current;
    if (!node) return;
    const sync = () => setAvail(node.getBoundingClientRect().width);
    sync();
    // ResizeObserver rather than a window listener: the invite page's own
    // layout (and the lab's scaled wall) can resize this box without the
    // window changing at all.
    const ro = new ResizeObserver(sync);
    ro.observe(node);
    return () => ro.disconnect();
  }, []);

  const GAP = 20;
  const bothFit = avail >= CARD_W * 2 + GAP;
  // Below the two-up width the pair wraps to one card per row, so the box it
  // needs is one card wide and two cards tall.
  const innerW = bothFit ? CARD_W * 2 + GAP : CARD_W;
  const innerH = bothFit ? CARD_H : CARD_H * 2 + GAP;
  const scale = avail > 0 ? Math.min(1, avail / innerW) : 1;

  return (
    <div ref={host} className="w-full">
      {/* The outer box reserves the painted footprint — `transform` does not
          affect layout, so the pair keeps its natural size inside. */}
      <div style={{ height: avail > 0 ? innerH * scale : undefined, overflow: 'hidden' }}>
        <div
          className="origin-top-left"
          style={{ width: innerW, transform: `scale(${scale})` }}
        >
          <CardPair {...props} />
        </div>
      </div>
    </div>
  );
}
